import Vue from 'vue';
import Vuex from 'vuex';
import axios from 'axios';

import { HEADER_MENU_API_URL, FOOTER_MENU_API_URL, IMAGES_API_URL } from '../constants/urls';
import { MY_NOMINEE_STORE_NAME } from '../constants/store';

import userModule from './modules/user';
import contextModule from './modules/context';
import phasesModule from './modules/phases';
import newsModule from './modules/news';
import nominationsModule from './modules/nominations';
import archiveModule from './modules/archive';
import subscribesModule from './modules/subscribes';
import textsModule from './modules/texts';
import vote from './modules/vote';
import loader from './modules/loader';
import partnersModule from './modules/partners';
import titlesModule from './modules/titles';
import aboutModule from './modules/about';
import advModule from './modules/adv';

Vue.use(Vuex);

function store() {
  const state = {
    path: null,
    myNominee: typeof localStorage !== 'undefined' ? (localStorage.getItem(MY_NOMINEE_STORE_NAME) || null) : null,
    visitedPaths: [],
    isMain: false,
    isMobile: false,
    loading: true,
    headerMenu: [],
    footerMenu: [],
    footerInfo: [],
    socialLinks: [],
    styling: {
      backgroundImage: '',
      defaultShareImage: '',
      footerBackgroundColor: '',
      headerBackgroundColor: '',
      imageBackgroundColorOpacity: 0,
      logotypeLightImage: '',
      logotypeDarkImage: '',
      subscriptionBackgroundImage: '',
      linkAndMenuLinkUnderlineColor: '',
      footerTextAndFooterHeaderMenuLinkColor: '',
      footerBlockHeaderColor: '',
    }
  };

  const actions = {
    setPath({ commit }, path) {
      commit('path', path);

      if (path === '/') {
        commit('isMain', true);
      } else {
        commit('isMain', false);
      }

      if (!state.visitedPaths.find((visited) => visited === path)) {
        commit('visitedPaths', path);
      }
    },

    setMyNominee({ commit }, myNominee) {
      commit('myNominee', myNominee)
    },

    setIsMobile({ commit }, isMobile) {
      if (typeof isMobile === 'boolean') {
        commit('isMobile', isMobile);
      }
    },

    async fetchHeaderMenu({ commit, getters }) {
      const url = getters['context/contextHost'] + HEADER_MENU_API_URL;

      try {
        const res = await axios({
          withCredentials: true,
          method: 'GET',
          url
        });

        commit('headerMenu', res.data.data.menuLinks);
      } catch (e) {
        console.error(e)
      }
    },

    async fetchFooterMenu({ commit, getters }) {
      const url = getters['context/contextHost'] + FOOTER_MENU_API_URL;

      try {
        const res = await axios({
          withCredentials: true,
          method: 'GET',
          url
        });

        commit('footerMenu', res.data.data.menuLinks);
        commit('footerInfo', res.data.data.informationBlock);
        commit('socialLinks', res.data.data.socialLinks);
      } catch (e) {
        console.error(e)
      }
    },

    async fetchStyling({ commit, getters }) {
      const url = getters['context/contextHost'] + IMAGES_API_URL;

      try {
        const res = await axios({
          withCredentials: true,
          method: 'GET',
          url
        });
        commit('styling', res.data.data);
      } catch (e) {
        console.error(e)
      }
    },

    setLoading({ commit }, params) {
      const link = params.link;
      const value = params.action;

      if (state.visitedPaths.find((visited) => visited === link)) {
        window.scrollTo(0, 0);
        commit('loading', false);
        return false;
      }

      commit('loading', value);
      return true;
    }
  };

  const mutations = {
    path(state, path) {
      state.path = path;
    },

    myNominee(state, value) {
      state.myNominee = value

      if (value) {
        localStorage.setItem(MY_NOMINEE_STORE_NAME, value);
      } else {
        localStorage.removeItem(MY_NOMINEE_STORE_NAME);
      }
    },

    visitedPaths(state, value) {
      state.visitedPaths.push(value);
    },

    isMain(state, value) {
      state.isMain = value;
    },

    isMobile(state, value) {
      state.isMobile = value;
    },

    headerMenu(state, value) {
      state.headerMenu = value;
    },

    footerMenu(state, value) {
      state.footerMenu = value;
    },

    footerInfo(state, value) {
      state.footerInfo = value;
    },

    socialLinks(state, value) {
      state.socialLinks = value;
    },

    styling(state, value) {
      state.styling = value;
    },

    loading(state, value) {
      state.loading = value;
    },
  };

  const getters = {
    path({ path }) {
      return path;
    },

    myNominee({ myNominee }) {
      return myNominee
    },

    isMain({ isMain }) {
      return isMain;
    },

    isMobile({ isMobile }) {
      return isMobile;
    },

    loading({ loading }) {
      return loading;
    },

    headerMenu({ headerMenu }) {
      return headerMenu;
    },

    footerMenu({ footerMenu }) {
      return footerMenu;
    },

    footerInfo({ footerInfo }) {
      return footerInfo;
    },

    socialLinks({ socialLinks }) {
      return socialLinks;
    },

    mainImage({ styling }) {
      return styling.backgroundImage;
    },

    mainImageOpacity({ styling }) {
      return styling.imageBackgroundColorOpacity;
    },

    headerBackgroundColor({ styling }) {
      return styling.headerBackgroundColor;
    },

    footerBackgroundColor({ styling }) {
      return styling.footerBackgroundColor;
    },

    defaultShareImage({ styling }) {
      return styling.defaultShareImage;
    },

    logotypeLightImage({ styling }) {
      return styling.logotypeLightImage;
    },

    logotypeDarkImage({ styling }) {
      return styling.logotypeDarkImage;
    },

    linkColor({ styling }) {
      return styling.linkAndMenuLinkUnderlineColor;
    },

    subscriptionBackgroundImage({ styling }) {
      return styling.subscriptionBackgroundImage;
    },

    menuLinkColor({ styling }) {
      return styling.footerTextAndFooterHeaderMenuLinkColor;
    },

    footerHeadingsColor({ styling }) {
      return styling.footerBlockHeaderColor;
    },

    headingsColor({ styling }) {
      return styling.pageAndBlockHeaderColor;
    },
  };

  return {
    state,
    actions,
    mutations,
    getters,
    modules: {
      nominationPage: nominationsModule(),
      archivePage: archiveModule(),
      phaseBlock: phasesModule,
      news: newsModule,
      subscribesBlock: subscribesModule,
      user: userModule,
      context: contextModule,
      vote: vote(),
      loader: loader(),
      texts: textsModule,
      partners: partnersModule,
      titles: titlesModule,
      about: aboutModule,
      adv: advModule
    },
  };
}

export default new Vuex.Store(store());



// WEBPACK FOOTER //
// ./src/store/index.js